/**
 * Runtime-safe string, URL, and data utilities.
 *
 * IMPORTANT: This module contains NO dependencies on SvelteKit runtime modules
 * (e.g., `$app/environment`, `$app/stores`, etc.) or browser-specific APIs that
 * are unavailable in test environments.
 *
 * This separation allows these helpers to be:
 * - Imported in unit tests (node/bun context) without breaking the test runner
 * - Used in both app code and shared utility modules
 * - Tested in isolation
 *
 * In contrast, sibling modules such as `unsafe-browser.ts` and `unsafe-dev.ts` import
 * browser-specific or SvelteKit runtime modules. Keep those dependencies there,
 * not here.
 *
 * If you need to add a new helper:
 * - No SvelteKit/browser-only imports → add here to `safe.ts`
 * - Requires browser APIs → add to `unsafe-browser.ts`
 * - Requires SvelteKit runtime/dev behavior → add to `unsafe-dev.ts`
 */

export function escapeHtml(input: string): string {
	return input.replaceAll(/["&'<>]/g, (match) => {
		switch (match) {
			case '"':
				return '&quot;';
			case '&':
				return '&amp;';
			case "'":
				return '&apos;';
			case '<':
				return '&lt;';
			case '>':
				return '&gt;';
			default:
				return match;
		}
	});
}

export function capitalize(val: string, level: 'words' | 'whole' = 'whole'): string {
	if (val.trim().length === 0) return val;

	if (level === 'whole') {
		return String(val).charAt(0).toUpperCase() + String(val).slice(1);
	} else {
		return val.replaceAll(/\w+/g, (w) => capitalize(w, 'whole'));
	}
}

export function escapeRegExp(s: string): string {
	return s.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
}

export function isUrlSafeStrict(
	url: string,
	options?: { allowHttp?: boolean; baseUrl?: string },
): boolean {
	try {
		const baseUrl =
			options?.baseUrl ??
			(typeof window !== 'undefined' ? window.location.href : 'https://localhost/');
		const parsedUrl = new URL(url, baseUrl);
		const allowHttp = options?.allowHttp ?? false;
		if (parsedUrl.protocol !== 'https:' && !(allowHttp && parsedUrl.protocol === 'http:')) {
			return false;
		}

		try {
			const unicodeControl = new RegExp('\\p{C}', 'u');
			if (unicodeControl.test(url)) return false;
		} catch {
			if (/[\u0000-\u001F\u007F]/.test(url)) return false;
		}

		return true;
	} catch {
		return false;
	}
}

export function stripQueryEquals(query: string): string {
	return query.replaceAll(/=(?=(&|$))/g, '');
}

/**
 * Returns a new query with the parameter (or flag with no '=') added.
 */
export function queryWithParam(query: string | undefined, key: string, value?: string): string {
	const param =
		value !== undefined
			? `${encodeURIComponent(key)}=${encodeURIComponent(value)}`
			: encodeURIComponent(key);
	return stripQueryEquals((query ?? '') + (query ? '&' : '?') + param);
}

/**
 * Returns a new query with the parameter (if present) removed.
 */
export function queryWithoutParam(query: string | undefined, key: string): string {
	if (!query) return '';
	const params = new URLSearchParams(query);
	params.delete(key);
	const r = params.toString();
	return r ? '?' + stripQueryEquals(r) : '';
}

const UUID = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;

/** Whether `value` is a UUID, as an ID read from a URL has to be before it is trusted. */
export function isUuid(value: string): boolean {
	return UUID.test(value);
}

/**
 * Returns a new URL instance with the query parameter (or flag with no '=') added.
 */
export function urlWithQueryParam(url: URL | string, key: string, value?: string): URL {
	const u = new URL(url);
	u.searchParams.delete(key);
	u.search = queryWithParam(u.search, key, value);
	return u;
}

/**
 * Returns a display string for raw code strings. Naively strips out non-word
 * characters unless hard-coded otherwise.
 */
export function display(raw?: string, capitalize?: 'whole' | 'words'): string | undefined {
	if (raw === undefined) return undefined;

	const maybeCapitalize = capitalize ? (v: string) => _capitalize(v, capitalize) : (v: string) => v;
	switch (raw) {
		case 'edit_session.thread':
			return display('thread', capitalize);
		default:
			const words = raw.split(/[_\W]+/g).filter((p) => p.length > 0);
			if (capitalize === 'words') return words.map((w) => _capitalize(w, 'whole')).join(' ');
			return maybeCapitalize(words.join(' '));
	}
}

const _capitalize = capitalize;

export const neverPromise = new Promise<never>(() => {});

// Parses a date string with optional microsecond precision in the fractional
// seconds, returning a Unix timestamp in seconds with microsecond precision.
export function parseTs(str: string) {
	if (/^\d+(?:\.\d+)?$/.test(str)) return parseFloat(str);
	const d = new Date(str);
	const [, frac] = str.match(/\.(\d{1,6})(?: GMT|Z|.*)?$/) || [, '0'];
	const micros = parseInt(frac.padEnd(6, '0').slice(3, 6));
	return d.getTime() / 1000 + micros / 1_000_000;
}

export function withSortedShallowKeys<T extends Record<string, unknown>>(
	obj: T,
	sort: (a: [string, unknown], b: [string, unknown]) => number = ([a], [b]) => a.localeCompare(b),
): Record<keyof T & string, T[keyof T]> {
	return Object.fromEntries(Object.entries(obj).sort(sort)) as Record<keyof T & string, T[keyof T]>;
}
